import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import './HalaRoboAdvanced.css';
import { getConfig } from '../runtimeConfig';

const API = getConfig().REACT_APP_API;
const AI_API = getConfig().REACT_APP_AI_API;

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a', '#ff6b6b', '#4ecdc4', '#6366f1', '#8b5cf6'];

interface Attachment {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  preview?: string;  // data URL for images
  content?: string;  // extracted text content for CSV/text files
  status: 'uploading' | 'ready' | 'error';
}

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  dashboard?: any;
  analysis?: any;
  attachments?: Attachment[];
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
}

interface HalaRoboAdvancedProps {
  onBack?: () => void;
}

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Voice Recognition Hook
const useVoiceRecognition = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      setIsSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event: any) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        setTranscript(finalTranscript || interimTranscript);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const startListening = useCallback(() => {
    if (recognitionRef.current && !isListening) {
      setTranscript('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  }, [isListening]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return { isListening, transcript, isSupported, startListening, stopListening, toggleListening, setTranscript };
};

function HalaRoboAdvanced({ onBack }: HalaRoboAdvancedProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'bot',
      content: `👋 **Welcome to HalaRobo AI** - Your Advanced Warehouse Intelligence Platform.

**🎯 What I Can Show You:**

**📊 Dashboards & Reports**
• Network overview with all warehouses
• Customer-specific dashboards
• Warehouse details & breakdowns
• Expiry reports with full item details
• Custom dashboards, tables, charts, and KPIs

**🔍 Detailed Queries**
• "Show DMM warehouse" - Full warehouse breakdown
• "Show CUSTOMER_NAME details" - Customer profile
• "Expired items in JUB" - Items with locations & bins
• "Compare JUB vs DMM" - Side-by-side comparison

**📦 Inbound/Outbound**
• "inbound" or "GR" - Today's goods receipts
• "outbound" or "dispatch" - Today's shipments

**📎 Attachments**
• Attach CSV, Excel, images, or PDFs
• Drag & drop or click 📎 to upload
• I'll analyze your data and create dashboards

**🎤 Voice Commands**
• Click the microphone to speak your query
• Say "Show dashboard" or "expired items"

How can I help you today?`,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
  const [currentDashboard, setCurrentDashboard] = useState<any>(null);
  const [aiServerStatus, setAiServerStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Voice Recognition
  const { isListening, transcript, isSupported, toggleListening, setTranscript } = useVoiceRecognition();

  // Update input when voice transcript changes
  useEffect(() => {
    if (transcript) {
      setInputMessage(transcript);
    }
  }, [transcript]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputMessage]);

  useEffect(() => {
    checkAIHealth();
    const interval = setInterval(checkAIHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkAIHealth = async () => {
    try {
      const res = await axios.get(`${AI_API}/health`, { timeout: 5000 });
      if (res.data.status === 'OK' || res.data.status === 'healthy') {
        setAiServerStatus('online');
      }
    } catch (error) {
      setAiServerStatus('offline');
    }
  };

  const toggleTableExpansion = (tableId: string) => {
    setExpandedTables(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tableId)) {
        newSet.delete(tableId);
      } else {
        newSet.add(tableId);
      }
      return newSet;
    });
  };

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputMessage;
    if (!textToSend.trim() && attachments.length === 0) return;

    if (aiServerStatus === 'offline') {
      alert('⚠️ AI Server is offline. Please check if the backend server is running.');
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: textToSend,
      timestamp: new Date(),
      attachments: attachments.length > 0 ? attachments : undefined
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setTranscript('');
    setIsLoading(true);
    setShowQuickActions(false);

    const newHistory: ConversationMessage[] = [
      ...conversationHistory,
      { role: 'user', content: textToSend }
    ];

    try {
      const response = await axios.post(`${AI_API}/chat`, {
        message: textToSend,
        conversationHistory: conversationHistory.slice(-6),
        attachments: attachments.map(a => ({
          name: a.name,
          type: a.type,
          content: a.content || null,
          size: a.size
        }))
      }, {
        timeout: 120000
      });

      if (response.data.success) {
        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'bot',
          content: response.data.response,
          timestamp: new Date(),
          dashboard: response.data.dashboard,
          analysis: response.data.analysis,
          usage: response.data.usage ? {
            inputTokens: response.data.usage.input_tokens,
            outputTokens: response.data.usage.output_tokens,
            totalTokens: response.data.usage.input_tokens + response.data.usage.output_tokens
          } : undefined
        };

        setMessages(prev => [...prev, botMessage]);

        if (response.data.dashboard) {
          setCurrentDashboard(response.data.dashboard);
        }

        setConversationHistory([
          ...newHistory,
          { role: 'assistant', content: response.data.response }
        ]);

        // Clear attachments after sending
        setAttachments([]);
      } else {
        throw new Error(response.data.error || 'Unknown error');
      }
    } catch (error: any) {
      let errorMsg = '❌ **Request Failed**\n\n';

      if (error.code === 'ECONNREFUSED') {
        errorMsg += '🔌 AI Server Not Running. Please start the backend server.';
        setAiServerStatus('offline');
      } else if (error.code === 'ECONNABORTED') {
        errorMsg += '⏳ Request timeout. Try a simpler query.';
      } else {
        errorMsg += error.response?.data?.error || error.message || 'An unexpected error occurred.';
      }

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: errorMsg,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Helper functions for attachments
  const getFileIcon = (type: string): string => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.includes('csv') || type.includes('excel') || type.includes('spreadsheet')) return '📊';
    if (type.includes('pdf')) return '📄';
    if (type.includes('json')) return '📋';
    if (type.includes('text')) return '📝';
    return '📁';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  // File validation constants
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_EXTENSIONS = ['.csv', '.txt', '.xlsx', '.xls', '.pdf', '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.json', '.xml', '.doc', '.docx'];

  // Process file and extract content
  const processFile = async (file: File): Promise<Attachment> => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const attachment: Attachment = {
      id,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'uploading'
    };

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      attachment.status = 'error';
      console.error(`File too large: ${file.name} (${formatFileSize(file.size)} > ${formatFileSize(MAX_FILE_SIZE)})`);
      return attachment;
    }

    // Validate file extension
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext) && !file.type.startsWith('image/') && !file.type.startsWith('text/')) {
      attachment.status = 'error';
      console.error(`Unsupported file type: ${file.name} (${ext})`);
      return attachment;
    }

    try {
      // Handle images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setAttachments(prev =>
            prev.map(a => a.id === id ? { ...a, preview: e.target?.result as string, status: 'ready' } : a)
          );
        };
        reader.onerror = () => {
          console.error('Error reading image file:', file.name);
          setAttachments(prev =>
            prev.map(a => a.id === id ? { ...a, status: 'error' } : a)
          );
        };
        reader.readAsDataURL(file);
        attachment.preview = '';
      }
      // Handle CSV/text files
      else if (file.type === 'text/csv' || file.type === 'text/plain' || file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          setAttachments(prev =>
            prev.map(a => a.id === id ? { ...a, content, status: 'ready' } : a)
          );
        };
        reader.onerror = () => {
          console.error('Error reading text file:', file.name);
          setAttachments(prev =>
            prev.map(a => a.id === id ? { ...a, status: 'error' } : a)
          );
        };
        reader.readAsText(file);
      }
      // Handle Excel files
      else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.type.includes('spreadsheet')) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const workbook = XLSX.read(e.target?.result, { type: 'array' });
            let content = '';
            workbook.SheetNames.forEach(sheetName => {
              const worksheet = workbook.Sheets[sheetName];
              const jsonData = XLSX.utils.sheet_to_json(worksheet);
              content += `Sheet: ${sheetName}\n${JSON.stringify(jsonData, null, 2)}\n\n`;
            });
            setAttachments(prev =>
              prev.map(a => a.id === id ? { ...a, content, status: 'ready' } : a)
            );
          } catch (err) {
            console.error('Error parsing Excel file:', err);
            setAttachments(prev =>
              prev.map(a => a.id === id ? { ...a, status: 'error' } : a)
            );
          }
        };
        reader.onerror = () => {
          console.error('Error reading Excel file:', file.name);
          setAttachments(prev =>
            prev.map(a => a.id === id ? { ...a, status: 'error' } : a)
          );
        };
        reader.readAsArrayBuffer(file);
      }
      // Handle PDF
      else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        attachment.status = 'ready';
      }
      // Default for other file types
      else {
        attachment.status = 'ready';
      }
    } catch (error) {
      console.error('Error processing file:', error);
      attachment.status = 'error';
    }

    return attachment;
  };

  // Handle file selection from input
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const files = input.files;
    if (!files) return;

    const fileList = Array.from(files);
    // Reset input immediately before async work (avoids null ref after React event pool recycle)
    input.value = '';

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const attachment = await processFile(file);
      setAttachments(prev => [...prev, attachment]);
    }
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  // Handle drag leave
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  // Handle drop
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      // Limit max files at once
      const maxFiles = 5;
      const fileList = Array.from(files).slice(0, maxFiles);
      if (files.length > maxFiles) {
        console.warn(`Only first ${maxFiles} files will be processed (${files.length} dropped)`);
      }

      for (const file of fileList) {
        const attachment = await processFile(file);
        setAttachments(prev => [...prev, attachment]);
      }
    } catch (error) {
      console.error('Error handling dropped files:', error);
    }
  };

  // Remove attachment
  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const formatMessageContent = (content: string) => {
    content = content.replace(/```json[\s\S]*?```/g, '');
    content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    content = content.replace(/^[•\-] /gm, '&nbsp;&nbsp;• ');
    content = content.replace(/\n/g, '<br/>');
    return content;
  };

  // Quick action buttons
  const quickActions = [
    { icon: '📊', label: 'Dashboard', query: 'Show me the warehouse dashboard' },
    { icon: '👥', label: 'Customers', query: 'Show customers' },
    { icon: '⚠️', label: 'Expired', query: 'Show expired items' },
    { icon: '📦', label: 'Inbound', query: 'Show today inbound' },
    { icon: '🚛', label: 'Outbound', query: 'Show today outbound' },
    { icon: '📈', label: 'Capacity', query: 'Show storage capacity' },
    { icon: '🔄', label: 'Compare', query: 'Compare all warehouses side by side' },
    { icon: '📋', label: 'Report', query: 'Generate a full warehouse report' },
  ];

  // Download functions
  const downloadAsPDF = async () => {
    if (!dashboardRef.current) return;
    try {
      const canvas = await html2canvas(dashboardRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`HalaRobo-Report-${new Date().toISOString().split('T')[0]}.pdf`);
      alert('✅ PDF exported successfully!');
    } catch (error) {
      alert('Failed to export PDF');
    }
  };

  const downloadAsPNG = async () => {
    if (!dashboardRef.current) return;
    try {
      const canvas = await html2canvas(dashboardRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `HalaRobo-Report-${new Date().toISOString().split('T')[0]}.png`;
          link.click();
          URL.revokeObjectURL(url);
          alert('✅ PNG exported successfully!');
        }
      });
    } catch (error) {
      alert('Failed to export PNG');
    }
  };

  const downloadAsExcel = () => {
    if (!currentDashboard) return;
    try {
      const workbook = XLSX.utils.book_new();

      if (currentDashboard.metrics?.length > 0) {
        const metricsData = currentDashboard.metrics.map((m: any) => ({
          'Metric': m.label,
          'Value': m.value
        }));
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(metricsData), 'Metrics');
      }

      if (currentDashboard.tables?.length > 0) {
        currentDashboard.tables.forEach((table: any, idx: number) => {
          if (table.data?.length > 0) {
            const sheetName = (table.title || `Table${idx + 1}`).substring(0, 31).replace(/[*?:/\\[\]]/g, '');
            XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(table.data), sheetName);
          }
        });
      }

      if (currentDashboard.charts?.length > 0) {
        currentDashboard.charts.forEach((chart: any, idx: number) => {
          if (chart.data?.length > 0) {
            const sheetName = `Chart_${idx + 1}`;
            XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(chart.data), sheetName);
          }
        });
      }

      XLSX.writeFile(workbook, `HalaRobo-Report-${new Date().toISOString().split('T')[0]}.xlsx`);
      alert('✅ Excel exported successfully!');
    } catch (error) {
      alert('Failed to export Excel');
    }
  };

  // KPI Card Renderer
  const renderKPI = (kpi: any, idx: number) => {
    const statusColors: { [key: string]: string } = {
      good: '#22c55e',
      warning: '#f59e0b',
      critical: '#ef4444',
      neutral: '#6366f1'
    };

    if (kpi.type === 'gauge') {
      const angle = (kpi.value / kpi.maxValue) * 180;
      return (
        <div key={idx} className="kpi-card kpi-gauge">
          <div className="kpi-header">
            <span className="kpi-icon">{kpi.icon}</span>
            <span className="kpi-title">{kpi.title}</span>
          </div>
          <div className="gauge-container">
            <div className="gauge-background">
              <div
                className="gauge-fill"
                style={{
                  transform: `rotate(${angle}deg)`,
                  background: `conic-gradient(${statusColors[kpi.status]} ${angle}deg, transparent ${angle}deg)`
                }}
              />
              <div className="gauge-center">
                <span className="gauge-value">{kpi.value}</span>
                <span className="gauge-unit">{kpi.unit}</span>
              </div>
            </div>
          </div>
          {kpi.details && (
            <div className="kpi-details">
              {kpi.details.map((d: any, i: number) => (
                <div key={i} className="kpi-detail-item">
                  <span className="detail-dot" style={{ background: d.color }}></span>
                  <span className="detail-label">{d.label}</span>
                  <span className="detail-value">{d.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (kpi.type === 'donut') {
      return (
        <div key={idx} className="kpi-card kpi-donut">
          <div className="kpi-header">
            <span className="kpi-title">{kpi.title}</span>
          </div>
          <div className="mini-donut-container">
            <div
              className="mini-donut"
              style={{
                background: `conic-gradient(${kpi.segments.map((s: any, i: number) => {
                  const prevPercent = kpi.segments.slice(0, i).reduce((sum: number, seg: any) => sum + (seg.value / kpi.total) * 100, 0);
                  const percent = (s.value / kpi.total) * 100;
                  return `${s.color} ${prevPercent}% ${prevPercent + percent}%`;
                }).join(', ')})`
              }}
            >
              <div className="mini-donut-center">
                <span>{kpi.total.toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="kpi-segments">
            {kpi.segments.map((s: any, i: number) => (
              <div key={i} className="segment-item">
                <span className="segment-dot" style={{ background: s.color }}></span>
                <span className="segment-label">{s.label}</span>
                <span className="segment-value">{s.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (kpi.type === 'progress') {
      return (
        <div key={idx} className="kpi-card kpi-progress">
          <div className="kpi-header">
            <span className="kpi-icon">{kpi.icon}</span>
            <span className="kpi-title">{kpi.title}</span>
          </div>
          <div className="progress-value-container">
            <span className="progress-value" style={{ color: statusColors[kpi.status] }}>
              {kpi.value}{kpi.unit}
            </span>
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{
                width: `${kpi.value}%`,
                background: `linear-gradient(90deg, ${statusColors[kpi.status]}, ${statusColors[kpi.status]}80)`
              }}
            />
          </div>
          {kpi.subtitle && <div className="kpi-subtitle">{kpi.subtitle}</div>}
        </div>
      );
    }

    return null;
  };

  // Insight Renderer
  const renderInsight = (insight: any, idx: number) => {
    const priorityColors: { [key: string]: string } = {
      high: '#ef4444',
      medium: '#f59e0b',
      low: '#6366f1'
    };
    const typeColors: { [key: string]: string } = {
      alert: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6'
    };

    // Handle case where insight might be a string
    if (typeof insight === 'string') {
      return (
        <div key={idx} className="insight-item insight-info" style={{ borderLeftColor: '#3b82f6' }}>
          <span className="insight-icon">💡</span>
          <span className="insight-text">{insight}</span>
        </div>
      );
    }

    // Safely extract text - handle nested objects
    const getInsightText = (text: any): string => {
      if (typeof text === 'string') return text;
      if (text && typeof text === 'object') {
        if (typeof text.text === 'string') return text.text;
        return JSON.stringify(text);
      }
      return String(text || '');
    };

    return (
      <div
        key={idx}
        className={`insight-item insight-${insight.type || 'info'}`}
        style={{ borderLeftColor: typeColors[insight.type] || '#6366f1' }}
      >
        <span className="insight-icon">{typeof insight.icon === 'string' ? insight.icon : '💡'}</span>
        <span className="insight-text">{getInsightText(insight.text)}</span>
        {insight.priority && (
          <span
            className="insight-priority"
            style={{ background: priorityColors[insight.priority] || '#6366f1' }}
          >
            {typeof insight.priority === 'string' ? insight.priority : 'info'}
          </span>
        )}
      </div>
    );
  };

  // Dashboard Renderer
  const renderDashboard = (dashboard: any, messageId: string) => {
    if (!dashboard) return null;

    return (
      <div ref={dashboardRef} className="dashboard-container">
        {/* Export Buttons */}
        <div className="dashboard-export-buttons">
          <button onClick={downloadAsPDF} className="export-btn pdf">
            <span>📄</span> PDF
          </button>
          <button onClick={downloadAsPNG} className="export-btn png">
            <span>🖼️</span> PNG
          </button>
          <button onClick={downloadAsExcel} className="export-btn excel">
            <span>📊</span> Excel
          </button>
        </div>

        {/* Dashboard Header */}
        <div className="dashboard-header">
          <h3>{dashboard.title}</h3>
          {dashboard.subtitle && <div className="dashboard-subtitle">{dashboard.subtitle}</div>}
          <div className="dashboard-meta">
            Generated: {new Date().toLocaleString()} | HalaRobo AI v3.0
          </div>
        </div>

        {/* KPI Cards Section */}
        {dashboard.kpis && dashboard.kpis.length > 0 && (
          <div className="kpis-grid">
            {dashboard.kpis.map((kpi: any, idx: number) => renderKPI(kpi, idx))}
          </div>
        )}

        {/* Smart Insights Section */}
        {dashboard.insights && dashboard.insights.length > 0 && (
          <div className="insights-section">
            <h4 className="section-title">
              <span>💡</span> Smart Insights
            </h4>
            <div className="insights-list">
              {dashboard.insights.map((insight: any, idx: number) => renderInsight(insight, idx))}
            </div>
          </div>
        )}

        {/* Metrics Grid */}
        {dashboard.metrics && dashboard.metrics.length > 0 && (
          <div className="metrics-grid">
            {dashboard.metrics.map((metric: any, idx: number) => (
              <div key={idx} className="metric-card" style={{ borderLeftColor: COLORS[idx % COLORS.length] }}>
                <div className="metric-label">{metric.label}</div>
                <div className="metric-value" style={{ color: COLORS[idx % COLORS.length] }}>
                  {metric.value}
                </div>
                {metric.status && typeof metric.status === 'string' && (
                  <div className={`metric-status metric-status-${metric.status}`}>
                    {typeof metric.icon === 'string' ? metric.icon : ''} {metric.status}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Charts */}
        {dashboard.charts && dashboard.charts.length > 0 && (
          <div className="charts-grid">
            {dashboard.charts.map((chart: any, idx: number) => renderChart(chart, idx))}
          </div>
        )}

        {/* Tables */}
        {dashboard.tables && dashboard.tables.length > 0 && (
          <div className="tables-section">
            {dashboard.tables.map((table: any, idx: number) => {
              const tableId = `${messageId}-table-${idx}`;
              const isExpanded = expandedTables.has(tableId);
              const rowCount = table.data?.length || 0;
              const displayRows = isExpanded ? table.data : table.data?.slice(0, 15);

              if (!table.data || table.data.length === 0) return null;

              const columns = Object.keys(table.data[0]);

              return (
                <div key={idx} className="table-card">
                  <div className="table-header">
                    <h4>
                      📋 {table.title}
                      <span className="record-count">{rowCount} records</span>
                    </h4>
                    {rowCount > 15 && (
                      <button onClick={() => toggleTableExpansion(tableId)} className="expand-btn">
                        {isExpanded ? '🔼 Show Less' : `🔽 Show All ${rowCount}`}
                      </button>
                    )}
                  </div>

                  <div className="table-wrapper" style={{ maxHeight: isExpanded ? '600px' : '400px' }}>
                    <table>
                      <thead>
                        <tr>
                          {columns.map((col, colIdx) => (
                            <th key={colIdx}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {displayRows?.map((row: any, rowIdx: number) => (
                          <tr key={rowIdx}>
                            {columns.map((col, colIdx) => {
                              const value = row[col];
                              const isNumeric = typeof value === 'number';
                              const isExpired = value === 'EXPIRED' || value === 'Expired';

                              return (
                                <td key={colIdx} className={`${isNumeric ? 'numeric' : ''} ${isExpired ? 'expired' : ''}`}>
                                  {isNumeric ? value.toLocaleString() : value || '-'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Insights */}
        {dashboard.insights && dashboard.insights.length > 0 && (
          <div className="insights-card">
            <h4>💡 Key Insights</h4>
            <ul>
              {dashboard.insights.map((insight: any, idx: number) => {
                // Safely extract text from insight
                let text: string;
                if (typeof insight === 'string') {
                  text = insight;
                } else if (insight && typeof insight.text === 'string') {
                  text = insight.text;
                } else if (insight && typeof insight.text === 'object' && insight.text?.text) {
                  text = insight.text.text;
                } else if (insight) {
                  text = JSON.stringify(insight.text || insight);
                } else {
                  text = '';
                }
                return (
                  <li key={idx} style={{ color: COLORS[idx % COLORS.length] }}>
                    {text}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Chart Renderer
  const renderChart = (chart: any, idx: number) => {
    // Handle Gauge Chart Type
    if (chart.type === 'gauge') {
      const statusColors: { [key: string]: string } = {
        good: '#22c55e',
        warning: '#f59e0b',
        critical: '#ef4444'
      };
      const angle = (chart.value / chart.maxValue) * 180;

      return (
        <div key={idx} className="chart-card chart-gauge">
          <h4>{chart.title}</h4>
          <div className="large-gauge-container">
            <div className="large-gauge">
              <div className="gauge-track"></div>
              <div
                className="gauge-progress"
                style={{
                  background: `conic-gradient(from 180deg, ${statusColors[chart.status]} ${angle}deg, transparent ${angle}deg)`,
                }}
              />
              <div className="gauge-inner">
                <span className="gauge-big-value" style={{ color: statusColors[chart.status] }}>
                  {chart.value}
                </span>
                <span className="gauge-big-unit">{chart.unit}</span>
                <span className="gauge-label">{chart.status === 'good' ? 'Healthy' : chart.status === 'warning' ? 'At Risk' : 'Critical'}</span>
              </div>
            </div>
            {chart.thresholds && (
              <div className="gauge-thresholds">
                {chart.thresholds.map((t: any, i: number) => (
                  <div key={i} className="threshold-item">
                    <span className="threshold-dot" style={{ background: t.color }}></span>
                    <span className="threshold-label">{t.label}</span>
                    <span className="threshold-value">{t.value}%+</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (!chart.data || chart.data.length === 0) {
      return (
        <div key={idx} className="chart-card">
          <h4>{chart.title}</h4>
          <div className="no-data">No data available</div>
        </div>
      );
    }

    const firstItem = chart.data[0];
    const keys = Object.keys(firstItem);
    const xField = keys.find(k => typeof firstItem[k] === 'string') || keys[0];
    const yField = keys.find(k => typeof firstItem[k] === 'number') || keys[1];
    const maxValue = Math.max(...chart.data.map((item: any) => item[yField] || 0));

    return (
      <div key={idx} className="chart-card">
        <h4>{chart.title}</h4>

        {chart.type === 'bar' && (
          <div className="bar-chart">
            {chart.data.slice(0, 15).map((item: any, i: number) => {
              const label = item[xField];
              const value = item[yField];
              const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;

              return (
                <div key={i} className="bar-item">
                  <div className="bar-labels">
                    <span className="bar-label">{label}</span>
                    <span className="bar-value" style={{ color: COLORS[i % COLORS.length] }}>
                      {typeof value === 'number' ? value.toLocaleString() : value}
                    </span>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill"
                      style={{
                        width: `${percentage}%`,
                        background: `linear-gradient(90deg, ${COLORS[i % COLORS.length]}, ${COLORS[(i + 1) % COLORS.length]})`
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {chart.type === 'pie' && (
          <div className="pie-chart">
            <div
              className="pie-visual"
              style={{
                background: `conic-gradient(
                  ${chart.data.map((item: any, i: number) => {
                    const value = item[yField];
                    const total = chart.data.reduce((sum: number, d: any) => sum + d[yField], 0);
                    const percentage = (value / total) * 100;
                    const prevPercentage = chart.data.slice(0, i).reduce((sum: number, d: any) => sum + (d[yField] / total) * 100, 0);
                    return `${COLORS[i % COLORS.length]} ${prevPercentage}% ${prevPercentage + percentage}%`;
                  }).join(', ')}
                )`
              }}
            >
              <div className="pie-center">
                {chart.data.reduce((sum: number, d: any) => sum + d[yField], 0).toLocaleString()}
              </div>
            </div>
            <div className="pie-legend">
              {chart.data.map((item: any, i: number) => {
                const label = item[xField];
                const value = item[yField];
                const total = chart.data.reduce((sum: number, d: any) => sum + d[yField], 0);
                const percentage = ((value / total) * 100).toFixed(1);

                return (
                  <div key={i} className="legend-item">
                    <div className="legend-color" style={{ background: COLORS[i % COLORS.length] }} />
                    <div className="legend-info">
                      <span className="legend-label">{label}</span>
                      <span className="legend-value">{value.toLocaleString()} ({percentage}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const clearConversation = () => {
    setMessages([messages[0]]);
    setConversationHistory([]);
    setCurrentDashboard(null);
    setExpandedTables(new Set());
    setShowQuickActions(true);
  };

  return (
    <div
      className="halarobo-container"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #0a0a0f 100%)',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        color: '#f8fafc',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Drag and Drop Overlay */}
      {isDragOver && (
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 9999,
          background: 'rgba(99, 102, 241, 0.15)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '3px dashed #6366f1',
          borderRadius: '16px',
          pointerEvents: 'none'
        }}>
          <div style={{ textAlign: 'center', color: '#c4b5fd' }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📁</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>Drop files here</div>
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>CSV, Excel, Images, PDF</div>
          </div>
        </div>
      )}
      {/* CSS Animations */}
      <style>{`
        @keyframes voicePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(244, 63, 94, 0.4); }
          50% { box-shadow: 0 0 0 15px rgba(244, 63, 94, 0); }
        }
        @keyframes wave {
          0%, 100% { transform: scaleY(0.5); }
          50% { transform: scaleY(1.5); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.1); opacity: 0; }
        }
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(30px, -30px) scale(1.05); }
          50% { transform: translate(-20px, 20px) scale(0.95); }
          75% { transform: translate(20px, 30px) scale(1.02); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes typing {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-8px); opacity: 1; }
        }
        .messages-area::-webkit-scrollbar { width: 6px; }
        .messages-area::-webkit-scrollbar-track { background: transparent; }
        .messages-area::-webkit-scrollbar-thumb { background: rgba(99, 102, 241, 0.3); border-radius: 3px; }
      `}</style>
      {/* Animated Background */}
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      {/* Header */}
      <header className="halarobo-header" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 32px',
        background: 'rgba(10, 10, 15, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        zIndex: 100,
        position: 'relative'
      }}>
        <div className="header-brand" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div className="logo-container" style={{ position: 'relative', width: '56px', height: '56px' }}>
            <img src="/halarobo-mascot.jpg" alt="HalaRobo" style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              objectFit: 'cover',
              border: '2px solid #6366f1',
              boxShadow: '0 0 20px rgba(99, 102, 241, 0.3)'
            }} />
            <div style={{
              position: 'absolute',
              inset: '-4px',
              borderRadius: '16px',
              border: '2px solid #6366f1',
              animation: 'pulse 2s ease-in-out infinite'
            }}></div>
          </div>
          <div className="brand-info">
            <h1 style={{
              fontSize: '1.5rem',
              fontWeight: 800,
              margin: 0,
              background: 'linear-gradient(135deg, #f8fafc, #818cf8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>HalaRobo AI</h1>
            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '2px 0 0 0' }}>Warehouse Intelligence Platform</p>
          </div>
        </div>

        <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '9999px',
            fontSize: '0.8125rem',
            fontWeight: 600,
            background: aiServerStatus === 'online' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(244, 63, 94, 0.1)',
            border: `1px solid ${aiServerStatus === 'online' ? '#10b981' : '#f43f5e'}`
          }}>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: aiServerStatus === 'online' ? '#10b981' : '#f43f5e',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}></span>
            <span>{aiServerStatus === 'online' ? 'AI Online' : aiServerStatus === 'offline' ? 'Offline' : 'Checking...'}</span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '9999px',
            fontSize: '0.8125rem',
            fontWeight: 500,
            background: isListening ? 'rgba(244, 63, 94, 0.15)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${isListening ? '#f43f5e' : 'rgba(255,255,255,0.1)'}`,
            color: isListening ? '#f43f5e' : '#94a3b8'
          }}>
            <span>🎤</span>
            <span>{isListening ? 'Listening...' : 'Voice Ready'}</span>
          </div>

          <button onClick={clearConversation} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            borderRadius: '9999px',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)'
          }}>
            <span>✨</span> New Chat
          </button>
          <button onClick={() => onBack ? onBack() : window.history.back()} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            borderRadius: '9999px',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            background: 'rgba(255,255,255,0.05)',
            color: '#94a3b8',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <span>←</span> Dashboard
          </button>
        </div>
      </header>

      {/* Chat Area */}
      <main className="chat-main">
        <div className="messages-area">
          {messages.map((message) => (
            <div key={message.id} className={`message-wrapper ${message.type}`}>
              <div className="message-bubble">
                <div className="message-avatar">
                  {message.type === 'bot' ? (
                    <img src="/halarobo-mascot.jpg" alt="HalaRobo" />
                  ) : (
                    <span>👤</span>
                  )}
                </div>
                <div className="message-body">
                  <div className="message-meta">
                    <span className="sender">{message.type === 'bot' ? 'HalaRobo AI' : 'You'}</span>
                    <span className="time">{message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div
                    className="message-text"
                    dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content) }}
                  />
                  {message.attachments && message.attachments.length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {message.attachments.map(att => (
                        <div key={att.id} style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '4px 10px', background: 'rgba(255,255,255,0.1)',
                          borderRadius: '8px', fontSize: '0.75rem', color: '#94a3b8'
                        }}>
                          {att.type.startsWith('image/') && att.preview ? (
                            <img src={att.preview} alt="" style={{ width: '32px', height: '32px', borderRadius: '4px', objectFit: 'cover' }} />
                          ) : (
                            <span>{getFileIcon(att.type)}</span>
                          )}
                          <span>{att.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {message.dashboard && renderDashboard(message.dashboard, message.id)}
                  {message.usage && (
                    <div className="token-usage">
                      <span>🎯 {message.usage.totalTokens.toLocaleString()} tokens</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message-wrapper bot">
              <div className="message-bubble">
                <div className="message-avatar">
                  <img src="/halarobo-mascot.jpg" alt="HalaRobo" />
                </div>
                <div className="message-body">
                  <div className="typing-indicator">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <span>Analyzing data...</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {showQuickActions && messages.length <= 2 && (
          <div className="quick-actions-panel">
            <h4>🚀 Quick Actions</h4>
            <div className="quick-actions-grid">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  className="quick-action-btn"
                  onClick={() => handleSendMessage(action.query)}
                  disabled={isLoading || aiServerStatus === 'offline'}
                >
                  <span className="action-icon">{action.icon}</span>
                  <span className="action-label">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Input Area */}
      <footer className="input-footer" style={{
        padding: '20px 32px',
        background: 'rgba(10, 10, 15, 0.95)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        position: 'relative',
        zIndex: 100
      }}>
        <div className="input-container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Attachments Bar */}
          {attachments.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '8px',
              padding: '10px 16px',
              background: 'rgba(20, 20, 30, 0.7)',
              borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
              borderRadius: '16px 16px 0 0',
              flexWrap: 'wrap',
              marginBottom: '12px'
            }}>
              {attachments.map(att => (
                <div key={att.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  background: att.status === 'error' ? 'rgba(244, 63, 94, 0.15)' : 'rgba(99, 102, 241, 0.15)',
                  border: att.status === 'error' ? '1px solid rgba(244, 63, 94, 0.5)' : '1px solid rgba(99, 102, 241, 0.3)',
                  borderRadius: '10px',
                  fontSize: '0.8125rem',
                  color: att.status === 'error' ? '#f87171' : '#c4b5fd',
                  maxWidth: '200px',
                  opacity: att.status === 'uploading' ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }}>
                  {att.type.startsWith('image/') && att.preview ? (
                    <img src={att.preview} alt="" style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover' }} />
                  ) : att.status === 'uploading' ? (
                    <span style={{ fontSize: '0.9rem' }}>⏳</span>
                  ) : att.status === 'error' ? (
                    <span style={{ fontSize: '0.9rem' }}>⚠️</span>
                  ) : (
                    <span>{getFileIcon(att.type)}</span>
                  )}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</span>
                  <span style={{ fontSize: '0.7rem', color: att.status === 'error' ? '#f87171' : '#64748b' }}>({formatFileSize(att.size)})</span>
                  {att.status === 'error' && (
                    <span style={{ fontSize: '0.65rem', color: '#f87171', whiteSpace: 'nowrap' }}>Failed to read</span>
                  )}
                  <button onClick={() => removeAttachment(att.id)} style={{
                    background: 'none', border: 'none', color: att.status === 'error' ? '#f87171' : '#f43f5e', cursor: 'pointer', fontSize: '1rem', padding: '0 2px'
                  }}>×</button>
                </div>
              ))}
            </div>
          )}

          <div className="input-wrapper" style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '12px',
            background: 'rgba(20, 20, 30, 0.9)',
            border: '2px solid rgba(99, 102, 241, 0.3)',
            borderRadius: attachments.length > 0 ? '0 0 24px 24px' : '24px',
            padding: '12px 16px',
            transition: 'all 0.3s ease'
          }}>
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                isListening
                  ? '🎤 Listening... Speak your query'
                  : aiServerStatus === 'offline'
                  ? '⚠️ AI Server is offline'
                  : 'Ask anything about your warehouse...'
              }
              rows={1}
              disabled={isLoading || aiServerStatus === 'offline'}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: isListening ? '#f43f5e' : '#f8fafc',
                fontSize: '1rem',
                fontFamily: 'inherit',
                lineHeight: 1.5,
                resize: 'none',
                maxHeight: '120px',
                padding: '8px'
              }}
            />

            <div className="input-actions">
              {/* File Input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,.gif,.txt,.json"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              {/* Attach Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                title="Attach files"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.15)',
                  background: attachments.length > 0 ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255,255,255,0.05)',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  fontSize: '1.2rem',
                  position: 'relative',
                  opacity: isLoading ? 0.5 : 1
                }}
              >
                📎
                {attachments.length > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: '#6366f1',
                    color: 'white',
                    fontSize: '0.65rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700
                  }}>{attachments.length}</span>
                )}
              </button>

              {/* Voice Button - Always show, with fallback */}
              <button
                className={`voice-btn ${isListening ? 'active' : ''}`}
                onClick={() => {
                  if (isSupported) {
                    toggleListening();
                  } else {
                    alert('Voice recognition is not supported in your browser. Try Chrome, Edge, or Safari.');
                  }
                }}
                disabled={isLoading || aiServerStatus === 'offline'}
                title={!isSupported ? 'Voice not supported in this browser' : isListening ? 'Stop listening' : 'Start voice input'}
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  border: isListening ? '3px solid #f43f5e' : '2px solid rgba(255,255,255,0.2)',
                  background: isListening ? 'rgba(244, 63, 94, 0.2)' : 'rgba(255,255,255,0.05)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  animation: isListening ? 'voicePulse 1.5s ease-in-out infinite' : 'none',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  {isListening ? (
                    <>
                      <span style={{ fontSize: '1.5rem', zIndex: 2 }}>🎤</span>
                      <div style={{ 
                        position: 'absolute', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '3px',
                        zIndex: 1 
                      }}>
                        <span style={{ width: '3px', height: '8px', background: '#f43f5e', borderRadius: '2px', animation: 'wave 0.8s ease-in-out infinite' }}></span>
                        <span style={{ width: '3px', height: '16px', background: '#f43f5e', borderRadius: '2px', animation: 'wave 0.8s ease-in-out infinite 0.15s' }}></span>
                        <span style={{ width: '3px', height: '8px', background: '#f43f5e', borderRadius: '2px', animation: 'wave 0.8s ease-in-out infinite 0.3s' }}></span>
                      </div>
                    </>
                  ) : (
                    <span style={{ fontSize: '1.5rem' }}>🎙️</span>
                  )}
                </div>
              </button>

              {/* Send Button */}
              <button
                className="send-btn"
                onClick={() => handleSendMessage()}
                disabled={isLoading || !inputMessage.trim() || aiServerStatus === 'offline'}
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: 'white',
                  fontSize: '1.25rem',
                  cursor: isLoading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                  opacity: isLoading || !inputMessage.trim() ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                {isLoading ? (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }}></div>
                ) : (
                  <span>➤</span>
                )}
              </button>
            </div>
          </div>

          <div className="input-hints">
            <span>💡 Try: "Show dashboard" • "Expired items in JUB" • "Compare warehouses" • "Top customers"</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HalaRoboAdvanced;